import { promiseWithResolvers } from "../../promiseWithResolvers";
import { err, isErr, type FormNode, type Result, type ZExtensionProtocol } from "../types";


export interface CollectEditsMetadata {
  /** The node dictate its own edit info collection; 
   * just keep in mind, the node is also responsible for invoking children's
   * validation routines (usually just by invoking safeInvokeNode())
   */
  overrideCollect?: (
    node: FormNode,
    safeInvokeNode: (node: FormNode<any, any>) => Promise<any>,
    safeChildrenCollection: Map<string, FormNode<any, any>>
  ) => Promise<any>;
  triggerValidate: () => Promise<void>; // if validation failed, it should throw
}

export type CollectEditsProtocol<R = any> = ZExtensionProtocol<'collectEdits', CollectEditsMetadata, R>


const protocolPerform = async (
  currNodeGetter: () => FormNode<any, any>, 
  metadata: CollectEditsMetadata, 
  safeChildrenCollection: Map<string, FormNode<any, any>>, 
  safeInvokeNode: (node: FormNode<any, any>) => Promise<any>
) => {
  if (!metadata) { return }
  const { triggerValidate, overrideCollect: overrideFn } = metadata
  
  // when validation error occurred at current level, don't throw out immediately,
  // continue invoke children collection to trigger their validations too,
  // then return the deferred error
  let _err: Result<any, any>|null = null
  
  try {
    await triggerValidate()
  } catch (error) {
    _err = err(new Error('Validation failed', { cause: JSON.stringify({ error, token: currNodeGetter().token, info: (error as any)?.cause }) }))
  }
  
  if (overrideFn) {
    try {
      const collected = await overrideFn(currNodeGetter(), safeInvokeNode, safeChildrenCollection)
      return _err ? _err : collected
    } catch (error) {
      throw new Error( 'CollectEdit failed at overrideFn', { cause: JSON.stringify({ error, token: currNodeGetter().token, info: (error as any)?.cause }) })    
    }
  }
  const edits = {...currNodeGetter().state.stage}

  for (const k of safeChildrenCollection.keys()) {
    const childNode = safeChildrenCollection.get(k)!
    const childEdits = await safeInvokeNode(childNode)
    if (isErr(childEdits)) {
      _err = childEdits
    } else {
      edits[k] = childEdits
    }
  }
  return _err ? _err : edits
}


export const CollectEditsProtocol = {
  invoke: <R>(invokeProtocol: (protocol: ZExtensionProtocol<string, any, any>) => Promise<void>) : Promise<R> => {
    const { promise, resolve, reject } = promiseWithResolvers<R>()
    const postProcess = (result: Result<R>) => {
      if (result.kind === 'ok') {
        resolve(result.value)
      } else {
        reject(result.error)
        throw result.error
      }
    }
    const protocol : CollectEditsProtocol<R> = {
      metadataKey: 'collectEdits' as const,
      perform: protocolPerform,
      postProcess,
    }
    invokeProtocol(protocol)
    return promise
  },

  metadataKey: 'collectEdits' as const
}

